import React, { useEffect } from 'react';

import { useLocation } from 'react-router-dom';
import HeadingItem from '@dashboard/components/HeadingItem';
import { usePreview } from '@dashboard/context/PreviewContext';
import clsx from 'clsx';
import Button from '@dashboard/components/Button';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import usePage from '@dashboard/hooks/usePage';
import usePageSaving from './usePageSaving';
import { LoaderCircle, Maximize2, Minimize2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import storage from '@dashboard/services/storage';
import PreviewPriceSelect from './PreviewPriceSelect';

export function getPreviewViewOptions(pageType) {
  if (pageType === 'event') {
    return [
      { value: 'checkout', label: 'Event' },
      { value: 'confirmationCheckoutMessage', label: 'Confirmation' },
      { value: 'upsell', label: 'Upsell' },
    ];
  }

  if (pageType === 'form') {
    return [
      { value: 'checkout', label: 'Form' },
      { value: 'confirmationCheckoutMessage', label: 'Confirmation' },
    ];
  }

  return [
    { value: 'checkout', label: 'Checkout' },
    { value: 'confirmationCheckoutMessage', label: 'Confirmation' },
    { value: 'upsell', label: 'Upsell' },
  ];
}

const PagePreviewNavigation = ({ setPreviewKey, isFullscreen = false }) => {
  const { page, isLoading, updatePage } = usePage();
  const isSaving = usePageSaving();
  const [previewState, previewDispatch] = usePreview();
  const { pathname } = useLocation();

  const { deviceSize, colorMode, type: viewType } = previewState;

  useEffect(() => {
    if (pathname.includes('/test')) {
      setPreviewKey();
    }
  }, [location]);

  const isCheckoutPreview = previewState.type === 'checkout';
  const isTestCheckoutPreview = previewState.type === 'test-checkout';
  const isCheckoutConfirmationPreview = previewState.type === 'confirmationCheckoutMessage';
  const isReminderEmailPreview = previewState.type === 'reminderEmailMessage';
  const isCheckoutConfirmationEmailPreview = previewState.type === 'confirmationEmailMessage';
  // Email previews are a server-rendered iframe — layout / device / color / view
  // controls don't affect them, so the toolbar strips down to price + fullscreen.
  const isEmailPreview = isCheckoutConfirmationEmailPreview || isReminderEmailPreview;
  const showReload = isTestCheckoutPreview;
  const isUpsellPreview = previewState.type === 'upsell';
  const isCheckoutAfterPayment = pathname.includes('/after-payment/confirmation');
  const showSavingState =
    isCheckoutPreview ||
    isCheckoutConfirmationPreview ||
    isUpsellPreview ||
    isReminderEmailPreview ||
    isCheckoutConfirmationEmailPreview;
  const previewViewOptions = getPreviewViewOptions(page.type);

  // Determine current layout type
  const currentLayoutType =
    page.usageType === 'hosted' ? 'hosted' : page.siteLayoutType === 'embed' ? 'embed' : 'popup';

  const handleLayoutChange = async newLayout => {
    const updates = {};

    if (newLayout === 'hosted') {
      updates.usageType = 'hosted';
    } else {
      updates.usageType = 'site';
      updates.siteLayoutType = newLayout === 'embed' ? 'embed' : 'popup';
    }

    try {
      await updatePage.mutateAsync(updates);

      // Store the preview settings in localStorage
      storage.local.setItem('last-usage-type', updates.usageType);
      if (updates.siteLayoutType) {
        storage.local.setItem('last-site-layout-type', updates.siteLayoutType);
      }

      // Update preview variant after successful update
      const newVariant =
        newLayout === 'hosted'
          ? 'hosted_page'
          : newLayout === 'embed'
            ? 'site_embed'
            : 'site_popup';
      previewDispatch({ type: 'set', value: { variant: newVariant } });
    } catch (error) {
      console.error('Failed to update layout type:', error);
    }
  };

  const handleViewTypeChange = newViewType => {
    previewDispatch({ type: 'setViewType', value: newViewType });
  };

  return (
    <div className="flex min-h-12 flex-wrap items-center gap-2 py-2 ml-px z-10">
      <div className="flex flex-wrap items-center gap-2">
        <HeadingItem>Preview</HeadingItem>

        {/* Layout type dropdown — checkout rendering, irrelevant to emails */}
        {!isLoading && !isEmailPreview && (
          <Select value={currentLayoutType} onValueChange={handleLayoutChange}>
            <SelectTrigger
              size="sm"
              className="bg-white dark:bg-background"
              aria-label="Preview layout type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hosted">Hosted page</SelectItem>
              <SelectItem value="embed">Embed</SelectItem>
              <SelectItem value="popup">Pop-up</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* View type dropdown — switches between page previews (checkout /
            confirmation / upsell). Hidden during test payment/submission and for
            email previews (their type isn't one of these options). */}
        {!isCheckoutAfterPayment && !isLoading && !isTestCheckoutPreview && !isEmailPreview && (
          <Select value={viewType} onValueChange={handleViewTypeChange}>
            <SelectTrigger
              size="sm"
              className="bg-white dark:bg-background"
              aria-label="Preview view type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {previewViewOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <PreviewPriceSelect />
      </div>

      {/* Preview mode toggles */}
      <div className="flex items-center gap-1 ml-auto">
        {/* Device size applies to emails too; color mode does not affect the email render */}
        <>
          {/* Device size toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-input/30 dark:ring-1 dark:ring-input rounded-md p-1">
            <button
              onClick={() => previewDispatch({ type: 'setDeviceSize', value: 'mobile' })}
              className={clsx(
                'p-1 rounded text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors',
                deviceSize === 'mobile' &&
                  'bg-white dark:bg-accent text-gray-900 dark:text-foreground shadow-sm',
              )}
              title="Mobile view"
            >
              <DevicePhoneMobileIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => previewDispatch({ type: 'setDeviceSize', value: 'tablet' })}
              className={clsx(
                'p-1 rounded text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors',
                deviceSize === 'tablet' &&
                  'bg-white dark:bg-accent text-gray-900 dark:text-foreground shadow-sm',
              )}
              title="Tablet view"
            >
              <DeviceTabletIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => previewDispatch({ type: 'setDeviceSize', value: 'desktop' })}
              className={clsx(
                'p-1 rounded text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors',
                deviceSize === 'desktop' &&
                  'bg-white dark:bg-accent text-gray-900 dark:text-foreground shadow-sm',
              )}
              title="Desktop view"
            >
              <ComputerDesktopIcon className="h-4 w-4" />
            </button>
          </div>

          {!isEmailPreview && (
            <div className="flex items-center bg-gray-100 dark:bg-input/30 dark:ring-1 dark:ring-input rounded-md p-1">
              <button
                onClick={() => previewDispatch({ type: 'setColorMode', value: 'light' })}
                className={clsx(
                  'p-1 rounded text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors',
                  colorMode === 'light' &&
                    'bg-white dark:bg-accent text-gray-900 dark:text-foreground shadow-sm',
                )}
                title="Light mode"
              >
                <SunIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => previewDispatch({ type: 'setColorMode', value: 'dark' })}
                className={clsx(
                  'p-1 rounded text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors',
                  colorMode === 'dark' &&
                    'bg-white dark:bg-accent text-gray-900 dark:text-foreground shadow-sm',
                )}
                title="Dark mode"
              >
                <MoonIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </>

        {/* Fullscreen toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-input/30 dark:ring-1 dark:ring-input rounded-md p-1">
          <button
            onClick={() => previewDispatch({ type: 'setFullscreen', value: !isFullscreen })}
            className="flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-1 text-xs font-medium text-gray-600 dark:text-muted-foreground transition-colors hover:text-gray-900 dark:hover:text-foreground"
            title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen (⌘P)'}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>
        </div>
      </div>

      {!isFullscreen && (
        <div className="flex items-center">
          {showReload && (
            <div className="">
              <Button
                size="small"
                appearance="light"
                className={clsx('gap-x-1 ml-4')}
                onClick={() => setPreviewKey()}
              >
                <ArrowPathIcon className="h-4 w-4" />
                Reload
              </Button>
            </div>
          )}

          {showSavingState && (
            <div className="flex items-center text-gray-500 dark:text-muted-foreground text-sm">
              <span className="w-12 mr-1">{isSaving ? 'Saving' : 'Saved'}</span>
              {isSaving ? (
                <LoaderCircle className="h-5 w-5 shrink-0 animate-spin" />
              ) : (
                <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-300" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PagePreviewNavigation;
